/**
 * Error Boundary
 *
 * React error boundary component that catches JavaScript errors in the component
 * tree below it and displays a fallback UI instead of crashing the application.
 *
 * @module routes/ErrorBoundary
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorPage } from '../pages';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for the ErrorBoundary component.
 */
export interface ErrorBoundaryProps {
  /**
   * Child components to render.
   */
  children: ReactNode;

  /**
   * Optional fallback component to render when an error occurs.
   * If not provided, defaults to ErrorPage.
   */
  fallback?: ReactNode;
}

/**
 * State for the ErrorBoundary component.
 */
interface ErrorBoundaryState {
  /**
   * Whether an error has been caught.
   */
  hasError: boolean;

  /**
   * The error that was caught, if any.
   */
  error: Error | null;
}

// =============================================================================
// Component
// =============================================================================

/**
 * ErrorBoundary - Catches JavaScript errors in component tree.
 *
 * This class component catches errors thrown during rendering, in lifecycle methods,
 * and in constructors of the whole tree below it. When an error is caught:
 *
 * 1. The error is logged for debugging
 * 2. The fallback UI (ErrorPage) is rendered
 * 3. Users can reset the error state and try again
 *
 * Note: Error boundaries do not catch errors in:
 * - Event handlers (use try-catch)
 * - Async code (promises, setTimeout)
 * - Server-side rendering
 * - Errors in the boundary itself
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <Routes>
 *     <Route path="/" element={<HomePage />} />
 *   </Routes>
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  /**
   * Called when a descendant component throws an error.
   * Updates state to trigger fallback UI render.
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Called after an error is caught.
   * Logs the error for debugging purposes.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo.componentStack);
  }

  /**
   * Resets the error state, allowing the children to render again.
   * Called when user wants to retry after an error.
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Render custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default to ErrorPage with reset capability
      return <ErrorPage error={error} resetError={this.handleReset} />;
    }

    return children;
  }
}
